import React from 'react';
import { Box } from '@mui/material';

import { ComingSoonHero } from '../../feedback/ComingSoonHero';
import { PageContainer } from '../../layout/PageContainer';
import { PageHeader } from '../../layout/PageHeader';

import type { ComingSoonPageProps } from './ComingSoonPage.types';

/**
 * ComingSoonPage Component
 *
 * A pre-assembled page component that combines PageContainer, PageHeader,
 * and ComingSoonHero for quick creation of "coming soon" feature pages.
 *
 * @example
 * ```tsx
 * <ComingSoonPage
 *   title="Patient Communications"
 *   heroTitle="Patient Communications"
 *   heroDescription="Coming soon"
 *   primaryAction={{
 *     label: "I'm Interested",
 *     href: "https://jotpsych.com/contact",
 *     external: true,
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <ComingSoonPage
 *   title="Bill Insurance"
 *   action={<Button>Your rules</Button>}
 *   heroTitle="Bill Insurance"
 *   heroDescription="Your notes. Our AI + humans. Clean claims, every time."
 *   primaryAction={{
 *     label: "Learn more",
 *     href: "/billing-info",
 *   }}
 * />
 * ```
 */
export const ComingSoonPage: React.FC<ComingSoonPageProps> = ({
  // PageContainer props
  maxWidth,
  fullHeight,
  sx,
  // PageHeader props
  title,
  subtitle,
  action,
  showHeader = true,
  // ComingSoonHero props
  icon,
  heroTitle,
  description: heroDescription,
  primaryAction,
  secondaryAction,
  useAvatar,
  // Custom content
  children,
  ...props
}) => {
  // Use heroTitle if provided, otherwise fall back to title (title is required from PageHeaderProps)
  // Ensure we always have a string value for ComingSoonHero title prop
  const displayHeroTitle = heroTitle ?? title ?? '';

  return (
    <PageContainer maxWidth={maxWidth} fullHeight={fullHeight} sx={sx} {...props}>
      {showHeader && <PageHeader title={title} subtitle={subtitle} action={action} />}
      {!showHeader && action && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 3,
          }}
        >
          {action}
        </Box>
      )}
      {children || (
        <ComingSoonHero
          icon={icon}
          title={displayHeroTitle}
          description={heroDescription}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          useAvatar={useAvatar}
        />
      )}
    </PageContainer>
  );
};

